<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :header="t('campaignsBulkList.actions.duplicateDialog.header')"
    :style="{ width: '450px' }"
  >
    <div class="p-fluid">
      <p class="mb-3">
        {{ t('campaignsBulkList.actions.duplicateDialog.message') }}
      </p>
      <div class="field">
        <label for="newCampaignName">{{ t('campaignsBulkList.actions.duplicateDialog.nameLabel') }}</label>
        <InputText
          id="newCampaignName"
          v-model="newCampaignName"
          type="text"
          :placeholder="t('campaignsBulkList.actions.duplicateDialog.namePlaceholder')"
        />
      </div>
    </div>
    <template #footer>
      <SpmButton
        :label="t('cancel')"
        icon="far fa-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('validate')"
        icon="far fa-check"
        class-style="p-button p-button-success"
        :disabled="!newCampaignName.trim() || isLoading"
        :loading="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { duplicateFlow, fetchFlowOperators } from '@/composables/flows/useFlowApi';
import { getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { store } from '@/store';
import { TypeCampaignEnum } from '@/types';
import type { CampaignActionPayload, DuplicateCampaignResult } from './types';

export default defineComponent({
  name: 'DuplicateBulkCampaignAction',

  components: {
    Dialog,
    InputText,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentPayload = ref<CampaignActionPayload | null>(null);
    const newCampaignName = ref('');

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentPayload.value = payload;
      newCampaignName.value = `${t('campaignPrefix')} - ${payload.campaignName}`;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      newCampaignName.value = '';
      isLoading.value = false;
    };

    /**
     * Execute duplication
     */
    const executeDuplication = async () => {
      if (!currentPayload.value) return;

      isLoading.value = true;

      try {
        const result = await duplicateFlow(TypeCampaignEnum.BULK, currentPayload.value.campaignId, newCampaignName.value);
        if (result.success && result.id) {
          await showToastSuccess(t('campaignsBulkList.actions.duplicateSuccess'));
          setRefreshScenariosList(true);

          const emitResult: DuplicateCampaignResult = {
            campaignId: currentPayload.value.campaignId,
            newName: newCampaignName.value,
          };
          emit('confirm', emitResult);
          close();
        } else {
          await showToastError(t('campaignsBulkList.actions.duplicateError'));
        }
      } catch (error) {
        await showToastError(t('campaignsBulkList.actions.duplicateError'));
      } finally {
        isLoading.value = false;
      }
    };

    /**
     * Confirm the action - check services first
     */
    const confirm = async () => {
      if (!currentPayload.value || !newCampaignName.value.trim()) return;

      isLoading.value = true;

      try {
        const operators = await fetchFlowOperators(
          TypeCampaignEnum.BULK,
          currentPayload.value.campaignId,
        );
        const notConfiguredServices = await getNotConfiguredServices(
          idShop.value,
          ['editScenarioCampaign', ...operators],
        );

        if (notConfiguredServices.length) {
          store.commit('general/setNotConfiguredServices', notConfiguredServices);
          store.commit('general/setIsServicesParametersModalVisible', true);
          store.commit('general/setServiceParameterSuccessAction', executeDuplication);
          isLoading.value = false;
        } else {
          await executeDuplication();
        }
      } catch (error) {
        await showToastError(t('campaignsBulkList.actions.duplicateError'));
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      newCampaignName,
      close,
      confirm,
    };
  },
});
</script>
